/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import fetchMiniCartData from '@/actions/miniCart';
import { SnackBarContext } from '@/components/SnackBarContext';
import { TableCell } from '@/components/TableCell/TableCell';
import { useAddToCart } from '@/features/orders/api/postAddToCart';
import type { AddToCartResponse } from '@/types/reduxStore/cartUpdate';
import { useHeaderData } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { useLabels } from '@/hooks/useLabels';
import { type ProductSkuDetails } from '@/features/product';
import { useDispatch } from '@/hooks/redux/useDispatch';
import { isAxiosError } from '@/utils/errorsHandling';
import logger from '@/utils/logger';
import { Button } from '@az/starc-ui';
import { useQueryClient } from '@tanstack/react-query';
import cx from 'classnames';
import { useRouter } from 'next/navigation';
import { use, useMemo, useState } from 'react';
import type { PDPComparisonAnalytics } from '@/types/analytics';
import sharedStyles from '../../sharedStyles.module.scss';
import { findFulfillmentOption } from '../../utils/findFulfillmentOption';
import { trackPDPComparisonAddToCart } from '../../utils/trackPDPComparisonAddToCart';
import styles from './styles.module.scss';
import FitmentButton from '@/components/AZCustomComponent/FitmentButton';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import { interpolateLabel } from '@/features/i18n/utils/interpolateLabel';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import ACESFitmentButton from '@/components/AZCustomComponent/ACESFitmentButton';
import { type VehicleFit } from '@/api/types/browse-search-types';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';

export type ShopNowButtonProps = {
  skuId: string;
  productId: string;
  partNumber: string | null;
  skuDetails: ProductSkuDetails | undefined;
  price: number | undefined;
  showFitmentBadge: boolean;
  fitsVehicle: boolean;
  vehicleFitment: VehicleFit | undefined;
  analyticsData: PDPComparisonAnalytics;
  openMiniCart: (data: AddToCartResponse & { quantity: string }) => void;
};

const labelMap = {
  addToCart: 'label_PDPComparison_addToCart',
  goToCart: 'label_PDPComparison_goToCart',
  addToCartError: 'label_PDPComparison_addToCartError',
  forPartNumber: 'label_PDPComparison_forPartNumber',
};

export function ShopNowButton({
  skuId,
  productId,
  partNumber,
  skuDetails,
  price,
  showFitmentBadge,
  fitsVehicle,
  vehicleFitment,
  analyticsData,
  openMiniCart,
}: ShopNowButtonProps) {
  const labels = useLabels(labelMap);
  const { data: headerData } = useHeaderData();
  const { acesEnabled } = useAcesFlags();
  const router = useRouter();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [addedToCart, setAddedToCart] = useState(false);
  const { showSnackBar } = use(SnackBarContext);
  const { mutateAsync: addToCartAsync, isPending: addToCartPending } = useAddToCart({});
  const { setPageSpecificData, setPageSpecificDataAsync } = usePageSpecificData();

  const { fulfillmentTypeId, availableQty, canAddToCart } = useMemo(() => {
    const options =
      skuDetails?.skuPricingAndAvailability.skuAvailabilityInfo.fulfillmentOptions ?? [];
    const fulfillmentOption = findFulfillmentOption(options);

    return {
      fulfillmentTypeId: fulfillmentOption?.fulfillmentTypeId,
      availableQty: fulfillmentOption?.availableQty,
      canAddToCart: fulfillmentOption !== null,
    };
  }, [skuDetails?.skuPricingAndAvailability.skuAvailabilityInfo.fulfillmentOptions]);

  const handleAddToCart = async () => {
    const vehicleId = getPreferredVehicle(headerData)?.catalogVehicleId ?? '0';
    const storeNumber = headerData?.storeNumber ?? '';
    const quantity = '1';
    try {
      const data = await addToCartAsync({
        skuId,
        productId,
        quantity,
        fulfillmentTypeId,
        vehicleId,
        storeNumber,
      });
      setAddedToCart(true);
      trackPDPComparisonAddToCart({
        skuId,
        partNumber,
        analyticsData,
        skuDetails,
        availableQty,
        fulfillmentTypeId,
        price,
        quantity,
      });
      await dispatch(fetchMiniCartData(queryClient));
      openMiniCart({ ...data, quantity });
    } catch (err) {
      showSnackBar(labels.addToCartError);
      logger.error({
        message: 'Error adding to cart on PDP comparison chart',
        meta: {
          error: isAxiosError(err) ? err.response : err,
        },
      });
    }
  };

  const addedToCartFlag =
    addedToCart || (skuDetails && skuDetails.quantityInCart > 0) ? true : false;

  const onShopNowClick = async () => {
    if (addedToCartFlag) {
      // Set analytics and await before navigation to ensure data is set
      await setPageSpecificDataAsync({
        productFindingMethod: 'Finder',
        finderFeature: 'PDP Comparison Chart',
      });
      router.push('/cart');
    } else {
      // Set analytics without awaiting for add-to-cart flow
      setPageSpecificData({
        productFindingMethod: 'Finder',
        finderFeature: 'PDP Comparison Chart',
      });
      await handleAddToCart();
    }
  };

  return (
    <TableCell className={cx(sharedStyles.comparisonCell, styles.shopNowCell)} align="center">
      <FlexGrid gap={4} flexDirection="column">
        {showFitmentBadge && !acesEnabled ? (
          <FitmentButton
            customClass={styles.comparisonFitmentButton}
            hasVehicle
            fitsVehicle={fitsVehicle}
            displayGenericFitmentMessage
            toggleShowYmme={null}
          />
        ) : null}
        {showFitmentBadge && acesEnabled ? (
          <ACESFitmentButton
            customClass={{ container: styles.comparisonFitmentButton }}
            vehicleFitment={vehicleFitment}
            displayGenericFitmentMessage
            toggleShowYmme={null}
            underlineVehicle={false}
          />
        ) : null}
        <Button
          className={styles.shopNowButton}
          disabled={!addedToCartFlag && !canAddToCart}
          loading={addToCartPending}
          variant={addedToCartFlag ? 'secondary' : 'primary'}
          onClick={onShopNowClick}
        >
          {addedToCartFlag ? labels.goToCart : labels.addToCart}
          {partNumber && (
            <span className="sr-only">
              {interpolateLabel(labels.forPartNumber, { partNumber })}
            </span>
          )}
        </Button>
      </FlexGrid>
    </TableCell>
  );
}
